import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import "./InsightsPage.css";

type IconProps = { className?: string };

const ChipIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    <rect x="19" y="19" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    {[8,16,24,32].map((n) => <path key={`t${n}`} d={`M${n+4} 7v6M${n+4} 35v6`} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>)}
    {[8,16,24,32].map((n) => <path key={`s${n}`} d={`M7 ${n+4}h6M35 ${n+4}h6`} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>)}
  </svg>
);

const VerifyIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path d="M9 25l9 9L39 13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 7a17 17 0 1 0 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" opacity=".65"/>
  </svg>
);

const LayoutIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <rect x="7" y="8" width="34" height="32" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M14 14h9v8h-9zM27 14h7v14h-7zM14 26h9v8h-9zM27 32h7" fill="none" stroke="currentColor" strokeWidth="1.6"/>
  </svg>
);

const AutomationIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path d="M15 12h18M15 24h18M15 36h18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
    <circle cx="19" cy="12" r="4" fill="#07111c" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="29" cy="24" r="4" fill="#07111c" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="23" cy="36" r="4" fill="#07111c" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

const EmergingIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path d="M10 33l9-10 7 6 12-15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M31 14h7v7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 39h28" stroke="currentColor" strokeWidth="1.5" opacity=".65"/>
  </svg>
);

const PerspectiveIcon: React.FC<IconProps> = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path d="M7 24s6-10 17-10 17 10 17 10-6 10-17 10S7 24 7 24z" fill="none" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="24" cy="24" r="5" fill="none" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const themes = [
  {
    icon: ChipIcon,
    title: "Silicon Design & Architecture",
    text: "Engineering approaches, design methodologies, and architectural considerations for modern semiconductor development.",
    slug: "silicon-design-architecture",
  },
  {
    icon: VerifyIcon,
    title: "Verification & Functional Validation",
    text: "Perspectives on verification planning, quality assurance, coverage strategies, and validation methodologies.",
    slug: "verification-functional-validation",
  },
  {
    icon: LayoutIcon,
    title: "Physical Design & Implementation",
    text: "Insights into implementation challenges, timing closure, power optimization, and advanced-node design.",
    slug: "physical-design-implementation",
  },
  {
    icon: AutomationIcon,
    title: "Design Automation & Engineering Productivity",
    text: "How automation, workflows, and engineering practices improve consistency, efficiency, and execution quality.",
    slug: "design-automation-productivity",
  },
  {
    icon: EmergingIcon,
    title: "Emerging Technologies",
    text: "Exploring developments in AI hardware, chiplets, heterogeneous integration, advanced packaging, and future silicon trends.",
    slug: "emerging-technologies",
  },
  {
    icon: PerspectiveIcon,
    title: "Industry & Engineering Perspectives",
    text: "Commentary on market developments, engineering leadership, talent evolution, and the changing semiconductor ecosystem.",
    slug: "industry-engineering-perspectives",
  },
];

const featured = [
  {
    kicker: "Engineering Insight",
    category: "Silicon Design & Architecture",
    title: "Designing for Reliability in Advanced Semiconductor Nodes",
    excerpt:
      "As process geometries shrink, reliability becomes a system-level engineering discipline. Explore the design choices, verification considerations, and implementation practices that help teams build robust silicon at advanced nodes.",
    readTime: "5 min read",
    date: "Engineering Perspective",
    slug: "designing-for-reliability-advanced-semiconductor-nodes",
    visual: "chip",
  },
  {
    kicker: "Technical Perspective",
    category: "Verification & Functional Validation",
    title: "Why Verification Strategy Matters More Than Verification Volume",
    excerpt:
      "Verification success is not measured by the number of tests alone. A clear strategy aligns risk, coverage, debug efficiency, and architecture understanding to find the issues that matter before they become expensive.",
    readTime: "6 min read",
    date: "Technical Viewpoint",
    slug: "why-verification-strategy-matters",
    visual: "wave",
  },
  {
    kicker: "Industry Viewpoint",
    category: "Emerging Technologies",
    title: "The Growing Role of AI in Semiconductor Product Development",
    excerpt:
      "AI is influencing architecture exploration, verification, physical implementation, validation, and engineering productivity. The opportunity is not simply automation, but better engineering decisions across the lifecycle.",
    readTime: "5 min read",
    date: "Industry Perspective",
    slug: "growing-role-ai-semiconductor-development",
    visual: "ai",
  },
];

const resources = [
  {
    index: "01",
    title: "Technical Articles",
    text: "In-depth discussions on semiconductor engineering methodologies, design practices, verification strategies, implementation challenges, and engineering best practices.",
  },
  {
    index: "02",
    title: "Engineering Case Studies",
    text: "Real-world engineering challenges, execution approaches, and lessons learned from complex semiconductor development programs.",
  },
  {
    index: "03",
    title: "Industry Perspectives",
    text: "Observations on technology evolution, market trends, engineering leadership, and the future of semiconductor innovation.",
  },
  {
    index: "04",
    title: "White Papers & Guides",
    text: "Structured reference documents that explore engineering frameworks, methodologies, and emerging technologies in greater depth.",
  },
  {
    index: "05",
    title: "Events & Conference Insights",
    text: "Key takeaways, engineering discussions, and technology highlights from industry conferences, seminars, and technical forums.",
  },
  {
    index: "06",
    title: "Engineering Explainers",
    text: "Easy-to-understand visual and technical content that simplifies complex semiconductor concepts and development processes.",
  },
];

const InsightsPage: React.FC = () => {
  return (
    <Fragment>
      <div className="insights-page inner-page">
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle
              pageTitle="Insights & Articles"
              pagesub="Insights & Articles"
            />

            {/* SECTION 1 — HERO */}
            <section className="ins-hero">
              <div className="ins-hero__grid" />
              <div className="ins-hero__glow ins-hero__glow--one" />
              <div className="ins-hero__glow ins-hero__glow--two" />

              <div className="ins-shell ins-hero__inner">
                <div className="ins-hero__copy">
                  <div className="ins-eyebrow">
                    <span />
                    ENGINEERING PERSPECTIVES
                  </div>

                  <h1>Engineering <strong>Perspectives</strong></h1>

                  <p>
                    The semiconductor industry continues to evolve through new architectures,
                    advanced process technologies, evolving design methodologies, and increasing
                    system complexity. Staying ahead requires more than technical capability—it
                    requires continuous learning, informed decision-making, and a deep understanding
                    of engineering challenges across the product development lifecycle.
                  </p>

                  <p>
                    At Digicomm Semiconductor, our perspectives are shaped by practical engineering
                    experience across silicon design, verification, physical implementation,
                    validation, and product realization. Through technical articles, engineering
                    viewpoints, industry observations, and knowledge-driven content, we share ideas
                    that contribute to better engineering decisions and more reliable semiconductor
                    products.
                  </p>

                  <a href="#perspectives" className="ins-primary-btn">
                    Explore Our Perspectives
                    <Arrow />
                  </a>
                </div>

                <div className="ins-hero__visual" aria-hidden="true">
                  <div className="ins-chip">
                    <span className="ins-chip__core">DIGICOMM</span>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <React.Fragment key={i}>
                        <i className="ins-chip__pin ins-chip__pin--top" style={{ left: `${14 + i * 10}%` }} />
                        <i className="ins-chip__pin ins-chip__pin--bottom" style={{ left: `${14 + i * 10}%` }} />
                        <i className="ins-chip__pin ins-chip__pin--left" style={{ top: `${14 + i * 10}%` }} />
                        <i className="ins-chip__pin ins-chip__pin--right" style={{ top: `${14 + i * 10}%` }} />
                      </React.Fragment>
                    ))}
                  </div>

                  <svg className="ins-hero__traces" viewBox="0 0 560 500">
                    <g fill="none" stroke="currentColor">
                      <path d="M40 110h95l50 45h70" />
                      <path d="M15 160h110l42 40h88" />
                      <path d="M38 390h90l60-55h67" />
                      <path d="M520 105h-93l-48 50h-73" />
                      <path d="M545 182h-110l-56 37h-73" />
                      <path d="M527 405h-90l-58-62h-73" />
                    </g>
                  </svg>

                  <div className="ins-wave">
                    {Array.from({ length: 34 }).map((_, i) => (
                      <i
                        key={i}
                        style={{
                          height: `${16 + Math.abs(Math.sin(i * 0.7)) * 54}px`,
                          opacity: `${0.3 + Math.abs(Math.sin(i * 0.45)) * 0.7}`,
                        }}
                      />
                    ))}
                  </div>

                  <div className="ins-hero__caption">
                    <span>ENGINEERING</span>
                    <span>KNOWLEDGE</span>
                    <span>IN PRACTICE</span>
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 2 */}
            <section className="ins-section ins-themes" id="perspectives">
              <div className="ins-shell">
                <div className="ins-section-head">
                  <div>
                    <div className="ins-eyebrow"><span /> KNOWLEDGE THEMES</div>
                    <h2>Explore Our <strong>Perspectives</strong></h2>
                  </div>

                  <p>
                    Our perspectives are organized around the engineering disciplines,
                    technologies, and industry trends shaping the future of semiconductor
                    product development. Whether you're exploring design methodologies,
                    verification strategies, emerging architectures, or engineering best
                    practices, you'll find insights grounded in practical experience and
                    technical understanding.
                  </p>
                </div>

                <div className="ins-theme-grid">
                  {themes.map((theme, idx) => {
                    const Icon = theme.icon;
                    return (
                      <Link
                        className="ins-theme-card"
                        to={`/insights?theme=${theme.slug}`}
                        key={theme.title}
                      >
                        <span className="ins-theme-card__number">0{idx + 1}</span>
                        <div className="ins-theme-card__icon"><Icon /></div>
                        <h3>{theme.title}</h3>
                        <p>{theme.text}</p>
                        <span className="ins-theme-card__explore">
                          Explore Insights <Arrow />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* SECTION 3 */}
            <section className="ins-section ins-featured">
              <div className="ins-featured__glow" />
              <div className="ins-shell">
                <div className="ins-section-head ins-section-head--light">
                  <div>
                    <div className="ins-eyebrow"><span /> FEATURED CONTENT</div>
                    <h2>Featured Engineering <strong>Perspectives</strong></h2>
                  </div>

                  <p>
                    Explore a curated selection of engineering perspectives that reflect the
                    technologies, methodologies, and execution practices shaping modern
                    semiconductor development. From technical deep dives to industry
                    observations, each perspective is intended to inform, challenge, and
                    support better engineering decisions.
                  </p>
                </div>

                <div className="ins-featured-grid">
                  {featured.map((article, idx) => (
                    <article className={`ins-article-card ins-article-card--${article.visual}`} key={article.slug}>
                      <Link className="ins-article-card__visual" to={`/insights/${article.slug}`}>
                        <span className="ins-article-card__index">0{idx + 1}</span>
                        <div className="ins-article-card__visual-chip">
                          {article.visual === "wave" ? <VerifyIcon /> : article.visual === "ai" ? <EmergingIcon /> : <ChipIcon />}
                        </div>
                        <div className="ins-article-card__visual-lines" />
                      </Link>

                      <div className="ins-article-card__body">
                        <span className="ins-article-card__kicker">{article.kicker}</span>
                        <span className="ins-article-card__category">{article.category}</span>
                        <h3>
                          <Link to={`/insights/${article.slug}`}>{article.title}</Link>
                        </h3>
                        <p>{article.excerpt}</p>
                        <div className="ins-article-card__meta">
                          <span>{article.readTime}</span>
                          <span>{article.date}</span>
                        </div>
                        <Link className="ins-text-link" to={`/insights/${article.slug}`}>
                          Read Perspective <Arrow />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="ins-center">
                  <Link className="ins-outline-btn" to="/insights">
                    View All Perspectives <Arrow />
                  </Link>
                </div>
              </div>
            </section>

            {/* SECTION 4 */}
            <section className="ins-section ins-resources">
              <div className="ins-shell">
                <div className="ins-section-head">
                  <div>
                    <div className="ins-eyebrow"><span /> KNOWLEDGE RESOURCES</div>
                    <h2>Knowledge Resources for <strong>Engineering Leaders</strong></h2>
                  </div>

                  <p>
                    Engineering knowledge takes many forms. Some challenges are best explored
                    through detailed technical articles, while others benefit from practical
                    case studies, industry viewpoints, visual explainers, or engineering guides.
                    As our knowledge library continues to grow, this section brings together
                    diverse resources designed to support informed decision-making across the
                    semiconductor development lifecycle.
                  </p>
                </div>

                <div className="ins-resource-grid">
                  {resources.map((resource) => (
                    <Link className="ins-resource" to="/insights" key={resource.title}>
                      <span className="ins-resource__index">{resource.index}</span>
                      <div className="ins-resource__rule" />
                      <div>
                        <h3>{resource.title}</h3>
                        <p>{resource.text}</p>
                      </div>
                      <span className="ins-resource__arrow"><Arrow /></span>
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            {/* SECTION 5 */}
            <section className="ins-conversation">
              <div className="ins-conversation__circuit" />
              <div className="ins-conversation__glow" />

              <div className="ins-shell ins-conversation__inner">
                <div className="ins-conversation__copy">
                  <div className="ins-eyebrow"><span /> ENGINEERING CONVERSATION</div>
                  <h2>Let's Continue the <strong>Engineering Conversation</strong></h2>

                  <p>
                    Every engineering challenge is unique. While our perspectives are intended
                    to share knowledge, the most valuable insights often emerge through
                    collaboration and real-world problem solving.
                  </p>

                  <p>
                    Whether you're exploring a new semiconductor product, scaling an engineering
                    program, strengthening an existing design team, or addressing a complex
                    technical challenge, Digicomm Semiconductor is ready to collaborate with you.
                    Together, we can turn engineering knowledge into engineering outcomes.
                  </p>

                  <Link className="ins-primary-btn" to="/contact">
                    Discuss Your Engineering Challenge <Arrow />
                  </Link>
                </div>

                <div className="ins-conversation__panel" aria-hidden="true">
                  <div className="ins-conversation__panel-chip"><ChipIcon /></div>
                  <span>KNOWLEDGE</span>
                  <i />
                  <span>COLLABORATION</span>
                  <i />
                  <span>OUTCOMES</span>
                </div>
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default InsightsPage;
